import { useState } from 'react';
import type { DrillView } from '../components/analytics/drillView';
import { useAppBack } from './useAppBack';

/**
 * Drill stack: the push/pop history of drill views. Deliberately URL-free —
 * the dashboard page syncs the CURRENT view into the URL itself; chat view
 * cards each own a private stack and must not touch the page URL.
 *
 * With `consumeBrowserBack` the browser's back button unwinds the drill
 * stack (finding 35): each press pops one level via the useAppBack
 * interception layer, so back activates the internal „← Atgal" instead of
 * leaving the page. Once the stack unwinds to its root, back navigates
 * normally again.
 */
export function useDrillStack(
  initial: DrillView | DrillView[],
  opts?: { consumeBrowserBack?: boolean },
) {
  const [stack, setStack] = useState<DrillView[]>(Array.isArray(initial) ? initial : [initial]);

  const push = (next: DrillView) => setStack(prev => [...prev, next]);
  const popTo = (index: number) => setStack(prev => prev.slice(0, index + 1));
  const reset = (view: DrillView = { kind: 'inbox' }) => setStack([view]);

  // Browser back = one drill level. `stack.length` is the pre-pop value of
  // THIS closure, so the return value (still consuming?) is exact.
  useAppBack({
    enabled: !!opts?.consumeBrowserBack && stack.length > 1,
    goBack: () => {
      popTo(stack.length - 2);
      return stack.length > 2;
    },
  });

  const view: DrillView = stack.at(-1) ?? { kind: 'overview' };

  return { stack, view, push, popTo, reset };
}
